import { Component, EventEmitter, Output, forwardRef, signal } from '@angular/core';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { CdkDragHandle } from '@angular/cdk/drag-drop';
import { MatChipInputEvent } from '@angular/material/chips';
import { TranslocoModule } from '@jsverse/transloco';
import { Subject, debounceTime } from 'rxjs';
import { MaterialModule } from '@ukam/ui-kit/material.module';
import { FilterFieldOperator } from '@ukam/core/filters/types/filter-field-operator.type';
import { FilterField } from '@ukam/core/filters/models/filter-field';
import { FilterFieldItem } from '@ukam/core/filters/models/filter-field-item';

interface IntFilterOperatorOption {
  value: FilterFieldOperator;
  label: string;
}

const INT_FILTER_OPERATORS: IntFilterOperatorOption[] = [
  { value: 'eq', label: 'Equal to' },
  { value: 'ne', label: 'Not equal to' },
  { value: 'gt', label: 'Greater than' },
  { value: 'gte', label: 'Greater than or equal to' },
  { value: 'lt', label: 'Less than' },
  { value: 'lte', label: 'Less than or equal to' },
  { value: 'btw', label: 'Between' },
  { value: 'in', label: 'In' },
  { value: 'nin', label: 'Not in' },
];

@Component({
  selector: 'app-int-filter',
  templateUrl: './int-filter.component.html',
  imports: [FormsModule, MaterialModule, TranslocoModule, CdkDragHandle],
  providers: [
    { provide: NG_VALUE_ACCESSOR, useExisting: forwardRef(() => AppIntFilterComponent), multi: true },
  ],
})
export class AppIntFilterComponent implements ControlValueAccessor {
  @Output() removed = new EventEmitter<void>();

  operators = INT_FILTER_OPERATORS;
  separatorKeys = [ENTER, COMMA];

  field: FilterField | null = null;
  operator: FilterFieldOperator | string | undefined;
  value: number | null = null;
  start: number | null = null;
  end: number | null = null;
  items: FilterFieldItem[] = [];

  disabled = signal(false);

  private onChange: (value: FilterField | null) => void = () => { };
  private onTouched: () => void = () => { };

  private changeSubject = new Subject<FilterField>();
  private changeSubscription = this.changeSubject.pipe(debounceTime(800)).subscribe(field => {
    this.onChange(field);
    this.onTouched();
  });

  get isRange(): boolean {
    return this.operator === 'btw';
  }

  get isList(): boolean {
    return this.operator === 'in' || this.operator === 'nin';
  }

  onOperatorChange(op: FilterFieldOperator | string | undefined): void {
    this.operator = op;
    this.value = null;
    this.start = null;
    this.end = null;
    this.items = [];
    this.emit({ operator: op, value: undefined, start: undefined, end: undefined, items: [] });
  }

  onValueChange(value: number | null): void {
    this.value = value;
    this.emit({ value: value == null ? undefined : `${value}` });
  }

  onStartChange(value: number | null): void {
    this.start = value;
    this.emit({ start: value == null ? undefined : `${value}` });
  }

  onEndChange(value: number | null): void {
    this.end = value;
    this.emit({ end: value == null ? undefined : `${value}` });
  }

  onChipInputTokenEnd(event: MatChipInputEvent): void {
    this.addItem(Number(event.value));
    event.chipInput?.clear();
  }

  removeItem(index: number): void {
    this.items = this.items.filter((_, i) => i !== index);
    this.emit({ items: this.items });
  }

  clear(): void {
    this.value = null;
    this.start = null;
    this.end = null;
    this.items = [];
    this.emit({ value: undefined, start: undefined, end: undefined, items: [] });
  }

  private addItem(raw: number): void {
    const code = raw;
    if (code == null || isNaN(Number(code)) || this.items.some(i => i.code === code)) return;

    this.items = [...this.items, { code: code, name: code.toString(), selected: true }];
    this.emit({ items: this.items });
  }

  private emit(patch: Partial<FilterField>): void {
    if (!this.field) return;
    this.field = { ...this.field, ...patch };
    this.changeSubject.next(this.field);
  }

  writeValue(value: FilterField | null): void {
    this.field = value ?? null;
    this.operator = this.field?.operator;
    this.value = this.toNumber(this.field?.value);
    this.start = this.toNumber(this.field?.start);
    this.end = this.toNumber(this.field?.end);
    this.items = this.field?.items ?? [];
  }

  registerOnChange(fn: (value: FilterField | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  private toNumber(raw: string | undefined | null): number | null {
    if (raw === undefined || raw === null || raw === '') return null;
    const n = Number(raw);
    return isNaN(n) ? null : n;
  }
}
